<template>
  <main>
    <!-- 
    ============================================================================
      HERO SECTION
    ============================================================================
    -->
    <div class="relative overflow-y-hidden overflow-x-hidden bg-[#0A1A38]">
      <!-- Figma-style Blurred Glow -->
      <div
        class="absolute top-[2%] left-[6%] w-[500px] h-[500px] bg-[#2AD1EE] rounded-full blur-[350px] opacity-15 z-0 pointer-events-none"
      ></div>

      <section
        id="home"
        class="h-full overflow-hidden relative scroll-mt-[100px] md:scroll-mt-[300px]"
      >
        <!-- Infinite Scrolling Cards (Right Side) -->
        <div
          class="absolute top-[-200px] right-0 xl:-right-[150px] 2xl:w-[1100px] h-[2000px] w-[500px] lg:w-[900px] xl:w-[900px] overflow-hidden pointer-events-none hidden xl:block z-20"
        >
          <!-- Outer wrapper applies right-facing slant -->
          <div class="rotated-wall-right">
            <!-- Inner wrapper handles diagonal scroll -->
            <div class="scroll-wrapper">
              <img
                src="@/assets/hero-cards.avif"
                alt="Scrolling cards showing various streamer website designs"
                width="950"
                height="1101"
                fetchpriority="high"
                class="hero-scroll-image"
              />
              <img
                src="@/assets/hero-cards.avif"
                alt=""
                width="950"
                height="1101"
                class="hero-scroll-image"
              />
              <img
                src="@/assets/hero-cards.avif"
                alt=""
                width="950"
                height="1101"
                class="hero-scroll-image"
              />
            </div>
          </div>
        </div>

        <!-- Content Container -->
        <div
          class="container mx-auto max-w-[1400px] relative z-30 flex flex-col items-center xl:items-start justify-center w-full px-6 lg:px-10 xl:px-20 pt-28 pb-20 sm:pt-32 sm:pb-28 md:pt-40 md:pb-32 lg:pt-44 lg:pb-36 xl:pt-32 xl:pb-48 2xl:pt-36 2xl:pb-56"
        >
          <!-- Logo Image (Small/Left-aligned) -->
          <div
            v-if="isPageLoaded"
            v-motion
            :initial="{ opacity: 0, x: -50, scale: 0.9 }"
            :enter="{
              opacity: 1,
              x: 0,
              scale: 1,
              transition: { type: 'spring', stiffness: 100, damping: 20, delay: 100 },
            }"
            class="flex justify-start mb-6"
          >
            <img
              src="@/assets/hero-zachdevworks.avif"
              class="w-auto h-[32px] sm:h-[30px] object-contain"
              alt="Zach Dev Works logo"
              width="200"
              height="32"
            />
          </div>

          <!-- Text Content -->
          <div
            v-if="isPageLoaded"
            class="flex flex-col justify-center items-center xl:items-start max-w-[800px]"
          >
            <h1
              v-motion
              :initial="{ opacity: 0, y: 50 }"
              :enter="{
                opacity: 1,
                y: 0,
                transition: { type: 'spring', stiffness: 80, damping: 15, delay: 300 },
              }"
              class="text-white text-4xl text-center xl:text-left sm:text-6xl md:text-7xl lg:text-7xl font-black font-bro-mega leading-[0.9] tracking-tighter uppercase"
            >
              Stand out and <br class="hidden md:block" />
              engage online.
            </h1>
            <p
              v-motion
              :initial="{ opacity: 0, y: 30 }"
              :enter="{
                opacity: 1,
                y: 0,
                transition: { type: 'spring', stiffness: 60, damping: 20, delay: 500 },
              }"
              class="mt-8 text-white/70 text-base text-center xl:text-left sm:text-lg lg:text-xl font-lato max-w-[600px]"
              style="line-height: 1.8 !important"
            >
              Zach Dev Works creates stunning landing pages and tailor-made platforms that
              elevate your online presence and help you achieve your unique goals.
            </p>

            <!-- Buttons -->
            <div
              v-motion
              :initial="{ opacity: 0, y: 20 }"
              :enter="{
                opacity: 1,
                y: 0,
                transition: { type: 'spring', stiffness: 100, damping: 15, delay: 700 },
              }"
              class="mt-12 flex flex-col sm:flex-row gap-6 sm:gap-8 items-center xl:items-start"
            >
              <!-- Get in Touch (Filled Gradient) -->
              <a
                href="https://discord.gg/smky9MMEGH"
                target="_blank"
                rel="noopener noreferrer"
                class="relative inline-flex items-center justify-center px-12 py-3 group overflow-visible"
                aria-label="Get in touch with us on Discord"
              >
                <!-- Skewed Background -->
                <div
                  class="absolute inset-0 bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] transform -skew-x-[20deg] transition-all duration-300 group-hover:scale-105 group-hover:brightness-110 shadow-[0_4px_15px_rgba(46,113,191,0.4)]"
                ></div>
                <!-- Text -->
                <span
                  class="relative z-10 font-bro-mega font-black text-white text-lg tracking-tighter uppercase"
                >
                  Get in touch!
                </span>
              </a>

              <!-- View Projects (Outline) -->
              <a
                href="#projects"
                class="relative inline-flex items-center justify-center px-12 py-3 group overflow-visible"
                aria-label="View our featured projects"
              >
                <!-- Skewed Border/Background -->
                <div
                  class="absolute inset-0 border border-[#2AD1EE]/50 bg-[#04022E]/40 transform -skew-x-[20deg] transition-all duration-300 group-hover:bg-[#2AD1EE]/10 group-hover:border-[#2AD1EE]"
                ></div>
                <!-- Text -->
                <span
                  class="relative z-10 font-bro-mega font-black text-[#2AD1EE] text-lg tracking-tighter uppercase transition-colors duration-300 group-hover:text-cyan-300"
                >
                  View Projects
                </span>
              </a>
            </div>

            <!-- Stats Section -->
            <div
              v-if="isPageLoaded"
              class="mt-12 flex flex-wrap justify-center xl:justify-start gap-10 sm:gap-16 lg:gap-20"
            >
              <div
                v-motion
                :initial="{ opacity: 0, y: 20 }"
                :enter="{
                  opacity: 1,
                  y: 0,
                  transition: { type: 'spring', stiffness: 100, damping: 20, delay: 900 },
                }"
                class="flex flex-col items-center xl:items-start"
              >
                <span class="text-white text-4xl sm:text-5xl font-black font-bro-mega"
                  >{{ displayUniqueDesigns }}+</span
                >
                <span
                  class="text-[#C9C9C9] text-xs sm:text-sm font-lato uppercase tracking-[0.2em] mt-2"
                  >Unique Designs</span
                >
              </div>
              <div
                v-motion
                :initial="{ opacity: 0, y: 20 }"
                :enter="{
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 100,
                    damping: 20,
                    delay: 1000,
                  },
                }"
                class="flex flex-col items-center xl:items-start"
              >
                <span
                  class="text-4xl sm:text-5xl font-black font-bro-mega bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent"
                  >{{ displayProjectsFinished }}+</span
                >
                <span
                  class="text-[#C9C9C9] text-xs sm:text-sm font-lato uppercase tracking-[0.2em] mt-2"
                  >Projects Finished</span
                >
              </div>
              <div
                v-motion
                :initial="{ opacity: 0, y: 20 }"
                :enter="{
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 100,
                    damping: 20,
                    delay: 1100,
                  },
                }"
                class="flex flex-col items-center xl:items-start"
              >
                <span class="text-white text-4xl sm:text-5xl font-black font-bro-mega"
                  >{{ displayHappyCustomers }}+</span
                >
                <span
                  class="text-[#C9C9C9] text-xs sm:text-sm font-lato uppercase tracking-[0.2em] mt-2"
                  >Clients</span
                >
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Static Divider (Image Only) -->
    <div
      class="absolute bottom-[-160px] sm:bottom-[-80px] md:bottom-[-150px] lg:bottom-[-230px] xl:bottom-[-260px] left-0 w-full z-40 pointer-events-none"
    >
      <img
        src="@/assets/divider.avif"
        alt=""
        width="1920"
        height="250"
        loading="lazy"
        class="w-full h-auto min-h-[100px] object-cover transform"
      />
    </div>

    <!-- 
    ============================================================================
      SERVICES & FEATURES WRAPPER
    ============================================================================
    -->
    <div class="relative bg-[#0A1A38] overflow-hidden">
      <!-- Shared Atmospheric Glows -->
      <!-- Left Glow (Bridging Services/Features) -->
      <div
        class="absolute -left-[25%] top-[35%] -translate-y-1/2 w-[1000px] h-[1000px] bg-[#2AD1EE] rounded-full blur-[300px] opacity-20 pointer-events-none z-0"
      ></div>

      <!-- Right Glow (Bridging Services/Features) -->
      <div
        class="absolute -right-[25%] top-[65%] -translate-y-1/2 w-[1000px] h-[1000px] bg-[#2AD1EE] rounded-full blur-[300px] opacity-20 pointer-events-none z-0"
      ></div>

      <!-- 
      ============================================================================
        SERVICES SECTION
      ============================================================================
      -->
      <section
        id="services"
        class="relative z-10 pt-10 md:pt-32 pb-16 md:pb-20 bg-transparent scroll-mt-[100px] md:scroll-mt-[300px]"
      >
        <!-- Content Wrapper -->
        <div class="relative z-20 container mx-auto px-6 pt-20 max-w-[1400px]">
          <!-- Title Section -->
          <div class="flex flex-col items-center text-center mb-16 sm:mb-20">
            <h2
              v-motion
              :initial="{ opacity: 0, y: -40 }"
              :visible-once="{
                opacity: 1,
                y: 0,
                transition: { type: 'spring', stiffness: 100, damping: 20 },
              }"
              class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black font-bro-mega text-white uppercase flex flex-col items-center"
            >
              <span
                >Services
                <span
                  class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent"
                  >Tailored</span
                ></span
              >
              <span class="mt-2">for you</span>
            </h2>
            <p
              v-motion
              :initial="{ opacity: 0, y: 40 }"
              :visible-once="{
                opacity: 1,
                y: 0,
                transition: { type: 'spring', stiffness: 80, damping: 15, delay: 200 },
              }"
              class="mt-8 text-[#C9C9C9] text-base sm:text-lg font-lato max-w-[650px]"
              style="line-height: 1.8 !important"
            >
              Purpose-built website solutions designed specifically for game streamers—
              focused on visibility, engagement, and long-term growth.
            </p>
          </div>

           <!-- Service Cards Grid -->
          <div
            class="flex flex-col md:flex-row gap-8 md:gap-4 lg:gap-8 justify-center items-center mt-12 mb-16"
          >
            <div
              v-for="(card, index) in serviceCards"
              :key="index"
              class="relative flex-1 w-full max-w-[420px] cursor-pointer transition-all duration-500 ease-out"
              :class="[
                hoveredServiceIndex === index ? 'z-30 scale-105 md:scale-110' : 'z-10 scale-95 opacity-80 hover:opacity-100'
              ]"
              @mouseenter="hoveredServiceIndex = index"
              @mouseleave="hoveredServiceIndex = 1"
            >
              <!-- Frame Background -->
              <img
                :src="getServiceFrame(index)"
                class="w-full h-auto object-contain transition-all duration-500"
                :alt="card.title"
                width="420"
                height="300"
                loading="lazy"
              />
              
              <!-- Text Content Overlay -->
              <div class="absolute inset-0 flex flex-col items-center justify-center p-8 text-center pointer-events-none">
                <h3 
                  class="text-white font-bro-mega font-black text-xl sm:text-2xl lg:text-3xl tracking-tighter uppercase mb-4"
                  :class="hoveredServiceIndex === index ? 'text-white' : 'text-white/90'"
                >
                  {{ card.title }}
                </h3>
                <p 
                  class="text-white font-lato text-sm sm:text-base lg:text-lg max-w-[300px] leading-relaxed"
                  :class="hoveredServiceIndex === index ? 'text-white/80' : 'text-white/60'"
                >
                  {{ card.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <!-- 
      ============================================================================
        FEATURES SECTION
      ============================================================================
      -->
      <section id="features" class="relative z-10 md:pt-16 pb-20 lg:pb-48  bg-transparent scroll-mt-[100px] md:scroll-mt-[300px]">
        <div class="relative z-20 container mx-auto px-6 text-center max-w-[1400px]">
          <h2
            v-motion
            :initial="{ opacity: 0, y: -20 }"
            :visible-once="{
              opacity: 1,
              y: 0,
              transition: { duration: 800, ease: 'easeOut' },
            }"
            class="text-4xl sm:text-6xl md:text-7xl lg:text-7xl font-black font-bro-mega text-white uppercase tracking-tight mb-12"
          >
            Features <span class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent">Breakdown</span> 
          </h2>

          <!-- Phase Buttons (Skewed Style) -->
          <div
            v-motion
            :initial="{ opacity: 0, y: 15 }"
            :visible-once="{
              opacity: 1,
              y: 0,
              transition: { duration: 800, ease: 'easeOut', delay: 100 },
            }"
            class="flex justify-center items-center gap-4 mb-16"
          >
            <button
              @click="activeFeaturesPhase = 1"
              class="relative px-10 py-3 group overflow-visible"
              aria-label="Show Phase 1 Features"
            >
              <div
                class="absolute inset-0 transform -skew-x-[20deg] transition-all duration-300"
                :class="
                  activeFeaturesPhase === 1
                    ? 'bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE]'
                    : 'border border-[#2AD1EE]/30 bg-[#0A1A38]'
                "
              ></div>
              <span
                class="relative z-10 font-bro-mega font-black text-lg tracking-tighter uppercase transition-colors"
                :class="
                  activeFeaturesPhase === 1
                    ? 'text-white'
                    : 'text-[#2AD1EE]/70 group-hover:text-[#2AD1EE]'
                "
              >
                Phase 1
              </span>
            </button>

            <button
              @click="activeFeaturesPhase = 2"
              class="relative px-10 py-3 group overflow-visible"
              aria-label="Show Phase 2 Features"
            >
              <div
                class="absolute inset-0 transform -skew-x-[20deg] transition-all duration-300"
                :class="
                  activeFeaturesPhase === 2
                    ? 'bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE]'
                    : 'border border-[#2AD1EE]/30 bg-[#0A1A38]'
                "
              ></div>
              <span
                class="relative z-10 font-bro-mega font-black text-lg tracking-tighter uppercase transition-colors"
                :class="
                  activeFeaturesPhase === 2
                    ? 'text-white'
                    : 'text-[#2AD1EE]/70 group-hover:text-[#2AD1EE]'
                "
              >
                Phase 2
              </span>
            </button>
          </div>

          <div
            v-motion
            :initial="{ opacity: 0, y: 15 }"
            :visible-once="{
              opacity: 1,
              y: 0,
              transition: { duration: 800, ease: 'easeOut', delay: 200 },
            }"
            class="mb-20"
          >
            <h3
              class="text-2xl md:text-3xl font-black text-white font-bro-mega uppercase"
            >
              Core Website Features
            </h3>
            <p class="mt-2 text-[#C9C9C9] text-base sm:text-lg font-lato">
              {{
                activeFeaturesPhase === 1
                  ? "Phase 1 - Streamer Website Essentials"
                  : "Phase 2 - Advanced Dynamic Platforms"
              }}
            </p>
          </div>

        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 w-full max-w-[1200px] mb-20 mx-auto min-h-[600px]"
          style="perspective: 2000px"
        >
  <div
    v-for="(feature, index) in activeFeatures"
    :key="feature.id"
    v-motion
    :initial="{
      opacity: 0,
      x: -200,
      rotateZ: -15,
      scale: 0.8,
      filter: 'blur(5px)',
    }"
    :visible-once="{
      opacity: 1,
      x: 0,
      rotateZ: 0,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        stiffness: 140,
        damping: 14,
        mass: 1,
        delay: index * 150,
      },
    }"
    class="group relative"
  >
    <img
      :src="require(`@/assets/${feature.image}`)"
      :alt="feature.name"
      width="380"
      height="250"
      loading="lazy"
      class="w-full h-auto object-contain transition-all duration-500 ease-out group-hover:scale-[1.05] group-hover:drop-shadow-[0_0_30px_rgba(42,209,238,0.3)]"
    />
    
    <div
      class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
    >
      <div class="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-[#2AD1EE] rounded-tl-xl"></div>
      <div class="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-[#2AD1EE] rounded-br-xl"></div>
    </div>
  </div>
</div>
       
      </div>

      <!-- Static Divider (Image Only) -->
      <div class="absolute bottom-0 left-0 w-full z-40 pointer-events-none">
        <img
          src="@/assets/divider-transparent.avif"
          alt=""
          width="1920"
          height="50"
          loading="lazy"
          class="w-full h-auto min-h-[50px] object-cover"
        />
      </div>
    </section>

    <!-- 
    ============================================================================
      HOUSE GAMES SECTION
    ============================================================================
    -->
    <section id="house-games" class="relative z-10 py-20 lg:py-32 bg-transparent scroll-mt-[100px] md:scroll-mt-[300px]">
      <div class="relative z-20 container mx-auto px-6 text-center max-w-[1400px]">
        <!-- Badge -->
        <div 
          v-motion
          :initial="{ opacity: 0, scale: 0.8 }"
          :visible-once="{ opacity: 1, scale: 1, transition: { duration: 600 } }"
          class="inline-block px-6 py-2 rounded-full border border-[#2ACAEA] bg-[#2ACAEA]/20 mb-6"
        >
          <span class="text-[#2ACAEA] text-xs font-bold uppercase tracking-widest">Highlights</span>
        </div>

        <!-- Title -->
        <h2
          v-motion
          :initial="{ opacity: 0, y: -20 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { duration: 800, ease: 'easeOut' },
          }"
          class="text-5xl md:text-7xl font-black font-bro-mega text-white uppercase tracking-tight mb-8"
        >
         House <span class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent">Games</span>
        </h2>

        <!-- Subtitle -->
        <p
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { duration: 800, ease: 'easeOut', delay: 100 },
          }"
          class="text-[#C9C9C9] text-base sm:text-lg font-lato max-w-[800px] mx-auto mb-20 leading-relaxed"
        >
          Turn your website into a high-engagement platform with interactive games designed to captivate users, boost retention, and elevate the overall experience.
        </p>

        <!-- Games Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 w-full max-w-[1200px] mx-auto">
          <div
            v-for="(game, index) in houseGames"
            :key="game.id"
            v-motion
            :initial="{
              opacity: 0,
              y: 50,
              scale: 0.9,
            }"
            :visible-once="{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                type: 'spring',
                stiffness: 100,
                damping: 20,
                delay: index * 150,
              },
            }"
            class="group relative"
          >
            <img
              :src="require(`@/assets/${game.image}`)"
              :alt="game.name"
              width="380"
              height="450"
              loading="lazy"
              class="w-full h-auto object-contain transition-all duration-500 ease-out group-hover:scale-[1.05] group-hover:drop-shadow-[0_0_30px_rgba(42,209,238,0.3)]"
            />
          </div>
        </div>
      </div>
    </section>
  </div>

    <!-- 
    ============================================================================
      PROJECTS & PRICING CONSOLIDATED SECTION
    ============================================================================
    -->
    <section
      id="projects-pricing"
      class="relative overflow-hidden py-24 md:py-32 bg-[#0A1A38]"
    >
      <!-- Unified Background Atmospheric Radials -->
      <div class="absolute inset-0 z-0">
        <!-- Top Left Cyan Glow -->
        <div
          class="absolute top-[25%] -left-[15%] w-[500px] h-[500px] bg-[#2AD1EE] rounded-full blur-[350px] pointer-events-none"
        ></div>

        <!-- Bottom Left Cyan Glow -->
        <div
          class="absolute bottom-[5%] -right-[15%] w-[500px] h-[500px] bg-[#2AD1EE] rounded-full blur-[350px] pointer-events-none"
        ></div>
      </div>

      <div id="projects" class="relative z-10 max-w-7xl mx-auto px-6 scroll-mt-[100px] md:scroll-mt-[300px]">
        <!-- -------------------------------------------------------------------
              PROJECTS PART
          -------------------------------------------------------------------- -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { duration: 800, ease: 'easeOut' },
          }"
          class="text-center mb-20"
        >
          <h2
            class="text-5xl md:text-7xl font-black font-bro-mega text-white uppercase tracking-tight leading-tight"
          >
            Featured <span class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent">Streamer</span>
            <br class="hidden md:block" />
            Projects
          </h2>
        </div>

        <!-- Phase Switches -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 15 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { duration: 800, ease: 'easeOut', delay: 200 },
          }"
          class="flex justify-center items-center gap-4 mb-20"
        >
          <button
            @click="activeProjectsPhase = 1"
            class="relative px-10 py-3 group overflow-visible focus:outline-none focus:ring-0"
            aria-label="Show Phase 1 Projects"
          >
            <div
              class="absolute inset-0 transform -skew-x-[20deg] transition-all duration-300"
              :class="
                activeProjectsPhase === 1
                  ? 'bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE]'
                  : 'border border-[#2AD1EE]/30 bg-[#0A1A38]'
              "
            ></div>
            <span
              class="relative z-10 font-bro-mega font-black text-lg tracking-tighter uppercase transition-colors"
              :class="
                activeProjectsPhase === 1
                  ? 'text-white'
                  : 'text-[#2AD1EE]/70 group-hover:text-[#2AD1EE]'
              "
            >
              Phase 1
            </span>
          </button>

          <button
            @click="activeProjectsPhase = 2"
            class="relative px-10 py-3 group overflow-visible focus:outline-none focus:ring-0"
            aria-label="Show Phase 2 Projects"
          >
            <div
              class="absolute inset-0 transform -skew-x-[20deg] transition-all duration-300"
              :class="
                activeProjectsPhase === 2
                  ? 'bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE]'
                  : 'border border-[#2AD1EE]/30 bg-[#0A1A38]'
              "
            ></div>
            <span
              class="relative z-10 font-bro-mega font-black text-lg tracking-tighter uppercase transition-colors"
              :class="
                activeProjectsPhase === 2
                  ? 'text-white'
                  : 'text-[#2AD1EE]/70 group-hover:text-[#2AD1EE]'
              "
            >
              Phase 2
            </span>
          </button>
        </div>

        <!-- Projects Grid -->
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-20 min-h-[300px]"
          style="perspective: 2000px"
        >
          <div
            v-for="(project, index) in paginatedProjects"
            :key="project.id"
            v-motion
            :initial="{
              opacity: 0,
              x: -200,
              rotateZ: -15,
              scale: 0.8,
              filter: 'blur(5px)',
            }"
            :visible-once="{
              opacity: 1,
              x: 0,
              rotateZ: 0,
              scale: 1,
              filter: 'blur(0px)',
              transition: {
                type: 'spring',
                stiffness: 140,
                damping: 14,
                mass: 1,
                delay: index * 150,
              },
            }"
            class="flex flex-col bg-[#0A1A38]/90 backdrop-blur-xl border border-white/10 rounded-[2rem] overflow-hidden group hover:border-[#2AD1EE]/40 hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(42,209,238,0.2)] transition-all duration-500 ease-out cursor-default relative"
          >
            <div
              class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            >
              <div
                class="absolute inset-0 border-[2px] border-[#2AD1EE]/20 rounded-[2rem]"
              ></div>
              <div
                class="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-[#2AD1EE] rounded-tl-[2rem]"
              ></div>
              <div
                class="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-[#2AD1EE] rounded-br-[2rem]"
              ></div>
            </div>

            <div class="p-5 pb-0">
              <div
                class="relative aspect-video rounded-3xl overflow-hidden border border-white/5 shadow-inner"
              >
                <img
                  :src="require(`@/assets/projects/${project.image}`)"
                  :alt="project.name"
                  width="380"
                  height="214"
                  loading="lazy"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />
                <div
                  class="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0A1A38]/90 to-transparent"
                ></div>
              </div>
            </div>

            <div
              class="relative p-8 pt-6 text-center flex-grow overflow-hidden flex flex-col justify-between"
            >
              <div class="relative z-10 mb-8">
                <h3
                  class="text-2xl md:text-3xl font-black text-white uppercase tracking-wider mb-2 font-bro-mega group-hover:text-[#2AD1EE] transition-colors duration-300"
                >
                  {{ project.name }}
                </h3>
                <p
                  class="text-[#C9C9C9]/60 text-xs md:text-sm uppercase tracking-[0.2em] font-medium leading-relaxed"
                >
                  Phase {{ activeProjectsPhase }} Project - Fully Customized<br />Website
                  Design
                </p>
              </div>

              <a
                :href="project.link"
                target="_blank"
                rel="noopener noreferrer"
                class="relative z-20 group/btn block mx-4 mt-auto transition-transform duration-300 active:scale-95"
                aria-label="Visit the live website for this project"
              >
                <div
                  class="skew-x-[-20deg] border border-[#2AD1EE]/30 bg-white/5 py-4 transition-all duration-300 group-hover/btn:bg-[#2AD1EE] group-hover/btn:border-[#2AD1EE] relative overflow-hidden shadow-[0_0_20px_rgba(42,209,238,0)] group-hover/btn:shadow-[0_0_30px_rgba(42,209,238,0.3)]"
                >
                  <div
                    class="absolute inset-0 -translate-x-full group-hover/btn:animate-shine-fast pointer-events-none bg-gradient-to-r from-transparent via-white/70 to-transparent z-0"
                  ></div>
                  <span
                    class="skew-x-[20deg] block font-black text-[#2AD1EE] group-hover/btn:text-[#0A1A38] text-xs sm:text-sm uppercase tracking-[0.3em] transition-colors duration-300 relative z-10"
                  >
                    Visit Site
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>

        <!-- View All Button -->
        <div 
          class="flex justify-center mt-12 mb-32"
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { delay: 300, duration: 800 },
          }"
        >
          <router-link
            to="/projects"
            class="relative px-12 py-4 group overflow-visible focus:outline-none"
            aria-label="View all projects"
          >
            <div class="absolute inset-0 bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] transform -skew-x-[20deg] shadow-[0_0_30px_rgba(42,209,238,0.3)] transition-all duration-300 group-hover:scale-105 group-hover:brightness-110"></div>
            <span class="relative z-10 font-bro-mega font-black text-white text-lg tracking-tighter uppercase">
              View All Projects
            </span>
          </router-link>
        </div>

        <!-- -------------------------------------------------------------------
              TRUSTED PARTNERS SECTION
          -------------------------------------------------------------------- -->
        <div 
          id="partners"
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { duration: 800, ease: 'easeOut' },
          }"
          class="mt-32 mb-40 text-center scroll-mt-[100px] md:scroll-mt-[300px]"
        >
          <h2 class="text-5xl md:text-7xl font-black font-bro-mega text-white uppercase tracking-tight leading-tight">
            Trusted <span class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent">Partners</span>
          </h2>
          <p class="mt-6 text-[#C9C9C9] text-base md:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
            Collaborating with reliable platforms to deliver smarter, more engaging experiences for casino slot streamers and their communities.
          </p>

          <div class="mt-16 max-w-[1240px] mx-auto px-4">
            <div class="relative group overflow-hidden rounded-xl border border-cyan-500/20 bg-[#1A2140]">
              <!-- Background Watermarks -->
              <img
                src="@/assets/myrtlebot-background.png"
                class="hidden md:block absolute -right-[250px] -bottom-[170px] w-[50%] h-auto object-contain z-0 pointer-events-none"
                alt=""
              />
              <img 
                src="@/assets/zach_dev_works_background.png" 
                class="hidden md:block absolute -left-[250px] -bottom-10 w-[72%] h-auto object-contain z-0 pointer-events-none" 
                alt="" 
              />

              <!-- Card Content -->
              <div class="relative z-10 flex flex-col md:flex-row items-center justify-between p-8 sm:p-10 md:p-14 lg:px-24 lg:py-20 gap-10 md:gap-12">
                <!-- Left: Text content -->
                <div class="flex-1 text-center md:text-left">
                  <h3 class="text-xl sm:text-2xl md:text-3xl font-black text-white font-bro-mega uppercase mb-4 md:mb-6 tracking-wide">
                    In Partnership with MyrtleBot
                  </h3>
                  <p class="text-white/80 text-sm sm:text-base md:text-lg leading-relaxed max-w-xl mb-8 md:mb-12 font-medium">
                    MyrtleBot powers automated engagement and community interaction tools, enhancing viewer participation and real-time activity across streamer platforms.
                  </p>

                  <a
                    href="https://myrtlebot.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="relative inline-flex items-center justify-center px-8 py-3 sm:px-10 sm:py-4 group/btn overflow-visible"
                    aria-label="Visit MyrtleBot website"
                  >
                    <!-- Skewed Background (Gradient Parallelogram) -->
                    <div class="absolute inset-0 bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] transform -skew-x-[20deg] transition-all duration-300 group-hover/btn:scale-105 group-hover/btn:brightness-110 shadow-[0_0_20px_rgba(46,113,191,0.3)]"></div>
                    <!-- Text -->
                    <span class="relative z-10 flex items-center gap-2 font-bro-mega font-black text-white text-xs sm:text-sm md:text-base uppercase tracking-tight">
                      Visit Myrtlebot.com
                      <span class="text-base sm:text-lg font-bold tracking-[-0.1em] ml-1">>></span>
                    </span>
                  </a>
                </div>

                <!-- Right: Logo (Shrunken) -->
                <div class="flex-shrink-0 w-full max-w-[200px] xs:max-w-[240px] sm:max-w-[260px] lg:max-w-[320px]">
                  <img
                    src="@/assets/myrtlebot-logo.avif"
                    alt="MyrtleBot Logo"
                    class="w-full h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.6)] transition-all duration-500 group-hover:scale-[1.03]"
                  />
                </div>
              </div>

              <!-- Corner Image Assets -->
              <div class="absolute bottom-0 left-0 w-16 h-16 sm:w-20 md:w-24 sm:h-20 md:h-24 pointer-events-none z-20">
                <img src="@/assets/left-corner.png" class="w-full h-full object-contain" alt="" />
              </div>
              <div class="absolute bottom-0 right-0 w-16 h-16 sm:w-20 md:w-24 sm:h-20 md:h-24 pointer-events-none z-20">
                <img src="@/assets/right-corner.png" class="w-full h-full object-contain" alt="" />
              </div>
            </div>
          </div>
        </div>

        <!-- -------------------------------------------------------------------
             PRICING PART
        -------------------------------------------------------------------- -->
        <!-- Section Title -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            transition: { duration: 800, ease: 'easeOut' },
          }"
          id="pricing"
          class="text-center mb-24 scroll-mt-[100px] md:scroll-mt-[300px]"
        >
          <h2
            class="text-5xl md:text-7xl font-black font-bro-mega text-white uppercase tracking-tight leading-tight"
          >
            Pricing & <span class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent">Packages</span>
          </h2>
          <p
            class="mt-6 text-[#C9C9C9] text-base md:text-lg font-medium max-w-2xl mx-auto leading-relaxed"
          >
            Purpose-built website solutions designed specifically for game
            streamers—focused on visibility, engagement, and long-term growth.
          </p>
        </div>

        <!-- Pricing Cards Grid -->
        <div
          class="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-0 lg:items-stretch"
        >
          <div
            v-for="(pkg, index) in pricingPackages"
            :key="pkg.id"
            v-motion
            :initial="{
              opacity: 0,
              y: 100,
              rotateZ: index === 0 ? -5 : index === 2 ? 5 : 0,
              scale: 0.9,
            }"
            :visible-once="{
              opacity: 1,
              y: 0,
              rotateZ: 0,
              scale: index === 1 ? 1.05 : 1,
              transition: {
                type: 'spring',
                stiffness: 120,
                damping: 15,
                delay: index * 200,
              },
            }"
            class="relative w-full max-w-sm flex flex-col transition-all duration-500 group"
            :class="[
              pkg.popular
                ? 'z-20 scale-100 lg:scale-105 border border-transparent'
                : 'z-10 bg-[#0A1A38]/40 border border-white/10',
              index === 0 ? 'lg:rounded-l-[2rem] lg:border-r-0 rounded-[2rem]' : '',
              index === 1 ? 'rounded-[2rem] mt-6 lg:mt-0' : '',
              index === 2 ? 'lg:rounded-r-[2rem] lg:border-l-0 rounded-[2rem] mt-12 lg:mt-0' : '',
            ]"
          >
            <!-- Highlight Design -->
            <div
              v-if="pkg.popular"
              class="absolute inset-0 bg-gradient-to-br from-[#2E71BF] to-[#2AD1EE] rounded-[2rem] shadow-[0_0_50px_rgba(42,209,238,0.3)] z-0 overflow-hidden"
            >
              <!-- Animated Shine Effect for Popular Card -->
              <div class="absolute inset-0 animate-card-shine"></div>
            </div>

            <!-- Card Content -->
            <div
              class="relative z-10 flex flex-col flex-grow p-8 md:p-10"
              :class="pkg.popular ? 'text-white' : 'text-gray-300'"
            >
              <!-- Badge - Centered at the top -->
              <div
                class="absolute -top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center"
              >
                <div
                  v-if="pkg.popular"
                  class="px-6 py-2 border border-white/20 rounded-full bg-[#2E71BF] shadow-lg backdrop-blur-md"
                >
                  <span class="text-[10px] font-black uppercase tracking-[0.2em] text-white whitespace-nowrap">Most Popular</span>
                </div>
              </div>
              <div class="mb-8 sm:mb-10 mt-6 sm:mt-4">
                <h3
                  class="text-xl md:text-2xl font-black uppercase tracking-wider mb-2 font-bro-mega"
                  :class="pkg.popular ? 'text-white' : 'text-white'"
                >
                  {{ pkg.name }}
                </h3>
                <div class="flex flex-col gap-1 mt-6">
                  <span v-if="pkg.startsAt" class="text-xs font-black uppercase tracking-widest opacity-60 -mb-1">Starts at</span>
                  <div>
                    <span class="text-4xl md:text-5xl font-black font-bro-mega"
                      >${{ pkg.price }}</span
                    >
                  </div>
                </div>
              </div>


              <p
                class="text-sm md:text-base leading-relaxed mb-8 opacity-80"
                :class="pkg.popular ? 'text-white' : 'text-[#C9C9C9]'"
              >
                {{ pkg.description }}
              </p>

              <div class="flex-grow mb-12">
                <p class="text-xs font-black uppercase tracking-[0.2em] mb-6 opacity-60">
                  Includes:
                </p>
                <ul class="space-y-5">
                  <li
                    v-for="(feat, fIdx) in pkg.features"
                    :key="fIdx"
                    class="flex items-start gap-3 group/f"
                  >
                    <span
                      class="mt-1 flex-shrink-0 w-4 h-4 rounded-full border border-current flex items-center justify-center"
                    >
                      <svg class="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M2 5L4 7L8 3"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <span class="text-sm md:text-base font-medium">{{ feat }}</span>
                  </li>
                </ul>
              </div>

              <a
                :href="pkg.link"
                target="_blank"
                rel="noopener noreferrer"
                class="relative mt-auto group/btn overflow-visible focus:outline-none"
                aria-label="Get the pricing package via Discord"
              >
                <div
                  class="skew-x-[-20deg] py-4 transition-all duration-300 relative overflow-hidden flex items-center justify-center shadow-lg"
                  :class="
                    pkg.popular
                      ? 'bg-white border border-white group-hover/btn:bg-[#0A1A38] group-hover/btn:border-[#0A1A38]'
                      : 'bg-[#2AD1EE]/10 border border-[#2AD1EE]/30 group-hover/btn:bg-[#2AD1EE] group-hover/btn:border-[#2AD1EE]'
                  "
                >
                  <div
                    class="absolute inset-0 -translate-x-full group-hover/btn:animate-shine-fast pointer-events-none bg-gradient-to-r from-transparent via-white/50 to-transparent"
                  ></div>
                  <span
                    class="skew-x-[20deg] block font-black text-sm md:text-base uppercase tracking-[0.2em] transition-colors duration-300"
                    :class="
                      pkg.popular
                        ? 'text-[#0A1A38] group-hover/btn:text-white'
                        : 'text-[#2AD1EE] group-hover/btn:text-[#0A1A38]'
                    "
                  >
                    Get This Package
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Divider Image -->
      <!-- <div class="relative w-full h-auto flex justify-center my-20">
        <img
          src="@/assets/divider.avif"
          alt=""
          width="1920"
          height="100"
          loading="lazy"
          class="w-full h-auto min-h-[100px] object-cover"
        />
      </div> -->

      <!-- <div
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{
          opacity: 1,
          y: 0,
          transition: { duration: 800, ease: 'easeOut' },
        }"
        class="text-center mb-12 px-4"
      >
        <h2
          class="text-4xl md:text-6xl font-black font-bro-mega text-white uppercase tracking-tight leading-tight mb-8"
        >
          Why Game Streamers Prefer <span class="text-[#2AD1EE]">Zach Dev Works</span>
        </h2>

        <div class="flex flex-wrap justify-center gap-3 md:gap-4 max-w-4xl mx-auto">
          <div
            v-for="(feature, index) in whyChooseFeatures"
            :key="index"
            v-motion
            :initial="{ opacity: 0, scale: 0.8 }"
            :visible-once="{
              opacity: 1,
              scale: 1,
              transition: { duration: 500, ease: 'easeOut', delay: index * 100 },
            }"
            class="px-4 py-2 bg-[#0A1A38]/60 border border-[#2AD1EE]/30 rounded-full backdrop-blur-sm flex items-center gap-2"
          >
            <img
              src="@/assets/gradient-star.avif"
              alt=""
              width="16"
              height="16"
              loading="lazy"
              class="w-4 h-4 object-contain"
            />
            <span class="text-xs md:text-sm text-white font-medium">{{ feature }}</span>
          </div>
        </div>

        <div class="relative mt-16 md:mt-24 pt-16 overflow-x-clip">
          <div
            class="flex transition-transform duration-700 ease-out"
            :style="{ transform: `translateX(-${currentTestimonialIndex * 100}%)` }"
          >
            <div
              v-for="(pair, pairIndex) in testimonialPairs"
              :key="pairIndex"
              class="w-full flex-shrink-0 px-4 md:px-6"
            >
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
                <div
                  v-for="(testimonial, index) in pair"
                  :key="index"
                  class="relative flex flex-col rounded-2xl p-8 md:p-10 border border-white/10 backdrop-blur-md bg-gradient-to-b from-white/[0.08] to-[#D9D9D9]/[0.05]"
                >
                  <div class="absolute -top-10 right-6 md:-top-14 md:right-10 z-20">
                    <img
                      src="@/assets/quotes.avif"
                      alt=""
                      width="96"
                      height="96"
                      loading="lazy"
                      class="w-16 h-16 md:w-24 md:h-24 object-contain"
                    />
                  </div>

                  <div class="flex gap-1 mb-6">
                    <svg
                      v-for="star in 5"
                      :key="star"
                      class="w-5 h-5 text-[#FDB241]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                      />
                    </svg>
                  </div>

                  <p
                    class="flex-grow text-[#E2E8F0] text-start text-base md:text-[1.05rem] leading-relaxed mb-8 font-light"
                  >
                    {{ testimonial.text }}
                  </p>

                  <div class="w-full h-px bg-white/10 mb-8"></div>

                  <div class="flex flex-col items-center justify-between">
                    <div class="flex items-center gap-4">
                      <div
                        class="w-14 h-14 rounded-full overflow-hidden border border-white/20"
                      >
                        <div
                          class="w-full h-full bg-slate-700 flex items-center justify-center text-white font-bold text-lg"
                        >
                          {{ testimonial.initials }}
                        </div>
                      </div>
                      <div class="text-left">
                        <h3 class="text-white font-semibold text-lg leading-tight">
                          {{ testimonial.name }}
                        </h3>
                        <p class="text-[#C9C9C9] text-sm">{{ testimonial.platform }}</p>
                      </div>
                    </div>

                    <div class="flex items-center gap-2 mt-10">
                      <img
                        src="@/assets/verified.avif"
                        alt="Verified Account"
                        width="24"
                        height="24"
                        loading="lazy"
                        class="w-6 h-6 object-contain"
                      />
                      <span class="text-white text-sm font-medium">Verified</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-center items-center gap-3 mt-16 md:mt-20">
              <button
                v-for="(_, index) in testimonialPairs"
                :key="index"
                @click="currentTestimonialIndex = index"
                class="h-3 rounded-full transition-all duration-500 ease-out"
                :aria-label="`Go to testimonial slide ${index + 1}`"
                :class="[
                  currentTestimonialIndex === index
                    ? 'w-10 bg-gradient-to-r from-[#2AD1EE] to-[#2E71BF] shadow-[0_0_15px_rgba(42,209,238,0.5)]'
                    : 'w-3 bg-white/30 hover:bg-white/50',
                ]"
              ></button>
          </div>
        </div>
      </div> -->
    </section>
  </main>
</template>

<script>
import { defineComponent } from "vue";
import { phase1Projects, phase2Projects } from "@/data/projects";

export default defineComponent({
  name: "MainPage",
  data() {
    return {
      hoveredServiceIndex: 1,
      isPageLoaded: false,
      displayUniqueDesigns: 0,
      displayProjectsFinished: 0,
      displayHappyCustomers: 0,
      serviceCards: [
        {
          title: "Enhanced Websites",
          description: "A simple, reliable website to establish your online presence.",
        },
        {
          title: "Enhanced Experience",
          description:
            "A polished website with smoother user experience and improved visual flow.",
        },
        {
          title: "Fully Customized",
          description:
            "A fully custom-built website designed entirely around your brand, stream style, and audience behavior.",
        },
      ],
      activeProjectsPhase: 1,
      currentProjectPage: 1,
      projectsPerPage: 6,
      activeFeaturesPhase: 1,
      // New Data for Features
      phase1Features: [
        { id: 'f1', name: "Custom Web Design", image: "custom-web-design-card.avif" },
        { id: 'f2', name: "Leaderboards", image: "leaderboard-card.avif" },
        { id: 'f3', name: "Challenges", image: "challenges-card.avif" },
        { id: 'f4', name: "Socials", image: "socials-card.avif" },
        { id: 'f5', name: "Max Video", image: "max-video-card.avif" },
      ],
      phase2Features: [
        { id: 'f6', name: "Virtual Store", image: "virtual-store-card.avif" },
        { id: 'f7', name: "Custom Challenges", image: "custom-challenges-card.avif" },
        { id: 'f8', name: "Guess The Balance", image: "guess-the-balance-card.avif" },
        { id: 'f9', name: "Guess The Payout", image: "guess-the-payout-card.avif" },
        { id: 'f10', name: "Admin Dashboard", image: "admin-dashboard-card.avif" },
        { id: 'f11', name: "Raffle System", image: "raffle-system-card.avif" },
      ],
      houseGames: [
        { id: 'hg1', name: "Roulette", image: "roulette-card.avif" },
        { id: 'hg2', name: "Mines", image: "mines-card.avif" },
        { id: 'hg3', name: "Coinflip", image: "coinflip-card.avif" },
      ],
      phase1Projects,
      phase2Projects,
      pricingPackages: [
        { id: 1, name: "Classic Web Package", price: "200", description: "A clean, reliable website that puts you online with ease.", features: ["Clean design based on chosen theme colors", "Full frontend and backend development", "Deployment to your purchased domain", "1-2 minor revisions"], popular: false, link: "https://discord.gg/smky9MMEGH" },
        { id: 2, name: "Fully Customized Package", price: "500", description: "From concept to launch, crafted entirely around your vision.", features: ["Fully customized UI/UX design", "Brand-focused layout and interactions", "Frontend and backend development", "Deployment to purchased domain", "3-4 minor revisions"], popular: true, startsAt: true, link: "https://discord.gg/smky9MMEGH" },
        { id: 3, name: "Premium Package", price: "450", description: "A refined design crafted for smoother user experience.", features: ["Improved UI/UX with flexible design options", "Frontend and backend development", "Deployment to purchased domain", "2-3 minor revisions"], popular: false, link: "https://discord.gg/smky9MMEGH" },
      ],
      whyChooseFeatures: [
        "Built specifically for casino slot streamers",
        "Scalable features as your channel grows",
        "Designed to convert viewers into active players",
        "Interactive systems that keep viewers engaged",
        "No generic templates — every site matches your brand",
      ],
      testimonials: [
        { name: "Natasha Romanoff", initials: "NR", platform: "Kick_Willow_TV", text: "Zach Dev has transformed my streaming experience..." },
        { name: "Tony Stark", initials: "TS", platform: "ironMan_TV", text: "Working with Zach Dev Works was an absolute game-changer..." },
        { name: "Steve Rogers", initials: "SR", platform: "Captain_America_Live", text: "The team at Zach Dev Works delivered beyond what I imagined..." },
        { name: "Steve Rogers", initials: "SR", platform: "Captain_America_Live", text: "The team at Zach Dev Works delivered beyond what I imagined..." },
      ],
      currentTestimonialIndex: 0,
      testimonialInterval: null,
    };
  },
  computed: {
    // Computed property to handle switching feature images based on active phase
    activeFeatures() {
      return this.activeFeaturesPhase === 1 ? this.phase1Features : this.phase2Features;
    },
    testimonialPairs() {
      const pairs = [];
      for (let i = 0; i < this.testimonials.length; i += 2) {
        pairs.push(this.testimonials.slice(i, i + 2));
      }
      return pairs;
    },
    filteredProjects() {
      return this.activeProjectsPhase === 1
        ? this.phase1Projects.filter((p) => p.display)
        : this.phase2Projects.filter((p) => p.display);
    },
    paginatedProjects() {
      // Show only first 6 projects on main page
      return this.filteredProjects.slice(0, 6);
    },
  },
  watch: {
    activeProjectsPhase() {
      this.currentProjectPage = 1;
    },
  },
  mounted() {
    this.testimonialInterval = setInterval(() => {
      if (this.testimonialPairs.length > 0) {
        this.currentTestimonialIndex =
          (this.currentTestimonialIndex + 1) % this.testimonialPairs.length;
      }
    }, 5000);

    // Sync with App.vue loader (4s timeout + 200ms buffer)
    this.$nextTick(() => {
      if (window.isAppLoaderFinished) {
        this.isPageLoaded = true;
        this.startCounting();
      } else {
        setTimeout(() => {
          this.isPageLoaded = true;
          this.startCounting();
        }, 4200);
      }
    });

    this.$emit("sections-ready");
  },
  beforeUnmount() {
    if (this.testimonialInterval) {
      clearInterval(this.testimonialInterval);
    }
  },
  methods: {
    startCounting() {
      this.animateValue(200, "displayUniqueDesigns", 2500);
      this.animateValue(30, "displayProjectsFinished", 2000);
      this.animateValue(100, "displayHappyCustomers", 2000);
    },
    animateValue(target, key, duration) {
      const startTime = performance.now();
      const update = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Smooth easeOutQuint
        const ease = 1 - Math.pow(1 - progress, 5);
        this[key] = Math.floor(ease * target);
        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          this[key] = target;
        }
      };
      requestAnimationFrame(update);
    },
    getServiceFrame(index) {
      if (this.hoveredServiceIndex === index) {
        return require("@/assets/services-container-center.avif");
      }
      if (index === 1) {
        return require("@/assets/services-container-center-inactive.avif");
      }
      if (index < 1) {
        return require("@/assets/services-container-left.avif");
      }
      return require("@/assets/services-container-right.avif");
    },
  },
});
</script>

<style scoped>
/* ✅ Right-facing slant, fixed positioning */
.rotated-wall-right {
  transform: rotate(19deg);
  transform-origin: center center;
  will-change: transform;
  overflow: hidden;
  position: relative;
}

/* ✅ Vertical scrolling only (no X drift) */
.scroll-wrapper {
  display: flex;
  flex-direction: column;
  animation: scroll-straight-up 40s linear infinite;
  will-change: transform;
}

/* 🚀 Fix for the Cutoff and Connection issues */
.hero-scroll-image {
  width: 100%;
  /* Slightly less than natural height (1101px) to crop black padding for "connection" */
  height: 1020px; 
  object-fit: cover;
  display: block;
  /* Scale up to ensure edges aren't cutoff by the 19deg rotation */
  transform: scale(1.15);
  transform-origin: center center;
}

/* 🔧 Only scroll vertically — prevents sideways drift */
@keyframes scroll-straight-up {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-33.333%);
  }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.phase-fade-enter-active,
.phase-fade-leave-active {
  transition: all 0.5s ease;
}

.phase-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.phase-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* ✅ Hero section adjustments for large & ultra-wide screens */
@media (min-width: 1280px) {
  section#home {
    min-height: 100vh;
    display: flex;
    align-items: center;
  }

  section#home > div.relative {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 6rem;
  }

  .rotated-wall-right {
    transform: rotate(17deg);
    right: -80px;
    width: 950px;
  }

  .scroll-wrapper img {
    max-width: 100%;
  }
}

/* ✅ Ultra-wide screens (1920px+) */
@media (min-width: 1600px) {
  section#home > div.relative {
    padding-left: 10rem;
  }

  .rotated-wall-right {
    transform: rotate(16deg);
    right: -120px;
    width: 1050px;
  }

  .scroll-wrapper {
    animation-duration: 50s; /* slower for cinematic smoothness */
  }

  img[alt="Zach Dev Works logo"] {
    width: 950px;
    height: auto;
  }

  h1 {
    font-size: 3.5rem; /* ~56px */
  }
}

/* Shine Animation for Buttons */
@keyframes shine-fast {
  0% {
    transform: translateX(-150%) skewX(30deg);
  }
  100% {
    transform: translateX(250%) skewX(30deg);
  }
}

.animate-shine-fast {
  animation: shine-fast 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Continuous Diagonal Shine Animation for Popular Card */
@keyframes card-shine {
  0% {
    background: linear-gradient(
      120deg,
      transparent 0%,
      transparent 40%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 60%,
      transparent 100%
    );
    transform: translateX(-100%) translateY(-100%);
  }
  100% {
    background: linear-gradient(
      120deg,
      transparent 0%,
      transparent 40%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 60%,
      transparent 100%
    );
    transform: translateX(100%) translateY(100%);
  }
}

.animate-card-shine {
  animation: card-shine 3s ease-in-out infinite;
  pointer-events: none;
}

</style>
